// components/Register/Register.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser, clearError } from '../../redux/slices/authSlice';
import classes from './Register.module.css';

const Register = () => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        dob: '',
        country: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordMatch, setPasswordMatch] = useState(true);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, error, isLoggedIn } = useSelector((state) => state.auth);

    // Redirect if already logged in
    useEffect(() => {
        if (isLoggedIn) {
            navigate('/dashboard', { replace: true });
        }
    }, [isLoggedIn, navigate]);

    // Clear error when component mounts
    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Check password match
    useEffect(() => {
        if (formData.confirmPassword) {
            setPasswordMatch(formData.password === formData.confirmPassword);
        }
    }, [formData.password, formData.confirmPassword]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!passwordMatch) {
            return;
        }

        const { confirmPassword, ...registrationData } = formData;
        dispatch(registerUser(registrationData));
    };

    const togglePasswordVisibility = (field) => {
        if (field === 'password') {
            setShowPassword(!showPassword);
        } else {
            setShowConfirmPassword(!showConfirmPassword);
        }
    };

    const isFormValid = () => {
        return Object.keys(formData).every(key =>
            key === 'confirmPassword' ? true : formData[key]
        ) && passwordMatch && formData.confirmPassword;
    };

    return (
        <div className={classes.registerPage}>
            <div className={classes.backgroundPattern}></div>

            <div className={classes.container}>
                <div className={classes.leftSection}>
                    <div className={classes.brandSection}>
                        <Link to="/" className={classes.logoLink}>
                            <h1 className={classes.logo}>Cent Capital</h1>
                        </Link>
                        <p className={classes.tagline}>Make sense of your cents</p>
                    </div>

                    <div className={classes.welcomeContent}>
                        <h2 className={classes.welcomeTitle}>Your Path to Wealth Starts Here</h2>
                        <p className={classes.welcomeSubtitle}>
                            Connect your accounts in 60 seconds. Get instant clarity on net worth, spending, and savings.
                        </p>

                        <div className={classes.features}>
                            <div className={classes.feature}>
                                <span className={classes.featureIcon}>🔒</span>
                                <span>Bank-level security</span>
                            </div>
                            <div className={classes.feature}>
                                <span className={classes.featureIcon}>📊</span>
                                <span>Real-time insights</span>
                            </div>
                            <div className={classes.feature}>
                                <span className={classes.featureIcon}>🎯</span>
                                <span>Goal tracking</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={classes.rightSection}>
                    <div className={classes.formContainer}>
                        <div className={classes.formHeader}>
                            <h3 className={classes.formTitle}>Create Your Account</h3>
                            <p className={classes.formSubtitle}>Join thousands building their financial future</p>
                        </div>

                        {error && (
                            <div className={classes.errorMessage}>
                                <span className={classes.errorIcon}>⚠</span>
                                {error}
                            </div>
                        )}

                        <form className={classes.form} onSubmit={handleSubmit}>
                            <div className={classes.formRow}>
                                <div className={classes.inputGroup}>
                                    <label htmlFor="first_name" className={classes.label}>First Name</label>
                                    <input
                                        type="text"
                                        id="first_name"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        className={classes.input}
                                        placeholder="Enter your first name"
                                        required
                                    />
                                </div>

                                <div className={classes.inputGroup}>
                                    <label htmlFor="last_name" className={classes.label}>Last Name</label>
                                    <input
                                        type="text"
                                        id="last_name"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        className={classes.input}
                                        placeholder="Enter your last name"
                                        required
                                    />
                                </div>
                            </div>

                            <div className={classes.inputGroup}>
                                <label htmlFor="email" className={classes.label}>Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={classes.input}
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>

                            <div className={classes.formRow}>
                                <div className={classes.inputGroup}>
                                    <label htmlFor="dob" className={classes.label}>Date of Birth</label>
                                    <input
                                        type="date"
                                        id="dob"
                                        name="dob"
                                        value={formData.dob}
                                        onChange={handleInputChange}
                                        className={classes.input}
                                        required
                                    />
                                </div>

                                <div className={classes.inputGroup}>
                                    <label htmlFor="country" className={classes.label}>Country</label>
                                    <select
                                        id="country"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        className={classes.select}
                                        required
                                    >
                                        <option value="">Select Country</option>
                                        <option value="IN">India</option>
                                        <option value="US">United States</option>
                                        <option value="UK">United Kingdom</option>
                                        <option value="CA">Canada</option>
                                        <option value="AU">Australia</option>
                                        <option value="DE">Germany</option>
                                        <option value="FR">France</option>
                                        <option value="SG">Singapore</option>
                                    </select>
                                </div>
                            </div>

                            <div className={classes.inputGroup}>
                                <label htmlFor="password" className={classes.label}>Choose a Password</label>
                                <div className={classes.inputWrapper}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        className={classes.input}
                                        placeholder="Create a strong password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className={classes.passwordToggle}
                                        onClick={() => togglePasswordVisibility('password')}
                                    >
                                        {showPassword ? '👁' : '👁‍🗨'}
                                    </button>
                                </div>
                            </div>

                            <div className={classes.inputGroup}>
                                <label htmlFor="confirmPassword" className={classes.label}>Confirm Password</label>
                                <div className={classes.inputWrapper}>
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        className={`${classes.input} ${!passwordMatch && formData.confirmPassword ? classes.inputError : ''}`}
                                        placeholder="Confirm your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className={classes.passwordToggle}
                                        onClick={() => togglePasswordVisibility('confirm')}
                                    >
                                        {showConfirmPassword ? '👁' : '👁‍🗨'}
                                    </button>
                                </div>
                                {!passwordMatch && formData.confirmPassword && (
                                    <span className={classes.validationError}>Passwords do not match</span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className={classes.submitButton}
                                disabled={loading || !isFormValid()}
                            >
                                {loading ? (
                                    <span className={classes.loadingContent}>
                                        <span className={classes.spinner}></span>
                                        Creating Account...
                                    </span>
                                ) : (
                                    'Start My Financial Journey'
                                )}
                            </button>
                        </form>

                        <div className={classes.divider}>
                            <span className={classes.dividerText}>or</span>
                        </div>

                        <button className={classes.googleButton}>
                            <svg className={classes.googleIcon} viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            Continue with Google
                        </button>

                        <div className={classes.footer}>
                            <p className={classes.footerText}>
                                Already have an account?{' '}
                                <Link to="/login" className={classes.footerLink}>
                                    Sign in here
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;